import type { MobileAdsInfo, MobileSFClientAPI } from '@amzn/safe-frame-client-mobilesfclient';
import { MashAPIImpl } from './Mash.js';
import { CommonApiImplementation } from './CommonApiImplementation.js';
import { ClientMessageSender } from './clientMessageSender.js';
import type { AdDetails } from '@amzn/safeframe-model';
import { ClientMessageListeners } from './clientMessageReceiver.js';
import type { MobileSupportedCommands } from '../host/api-client/MobileSupportedCommands.js';
import { getUserNetworkConnectionInfo } from './service/Mash.js';

export class MobileSFClientAPIImplementation extends CommonApiImplementation implements MobileSFClientAPI {
    readonly mash: MashAPIImpl;

    constructor(cms: ClientMessageSender, o: AdDetails, mL: ClientMessageListeners) {
        super(cms, o, mL);
        this.mash = new MashAPIImpl(this, cms, o);
    }
    onPageEvent = {
        callback: () => {
            /**/
        },
        execute: (eventName: string, callback: () => void) => {
            this.logAPIInvocation('onPageEvent');
            this.onPageEvent.callback = callback;
            this.cms.sendMessage<MobileSupportedCommands['onPageEvent']>('onPageEvent', { eventName: eventName });
        },
        update: () => {
            if (typeof this.onPageEvent.callback === 'function') {
                this.onPageEvent.callback();
            }
        },
    };

    /**
     * @func renderClickThroughAppAds
     * @desc Renders click-through app ads for SKAdNetwork attribution on iOS.
     *  * This handles the SKAN click-through attribution when a user clicks on an app ad.
     *  * It uses the MASH API to communicate with the native iOS layer for proper SKAdNetwork attribution.
     *
     * @see https://developer.apple.com/documentation/storekit/skadnetwork
     * @param url {URL} The fallback url used to pass to MASH API
     *
     * @example
     *  const url = new URL("https://apps.apple.com/us/app/tapier/id6476941533?aref=0LAPHaEBAS");
     *  renderClickThroughAppAds(url);
     *
     * Note, adDetails object is passed by default in sendMessage which includes SKAdNetwork object to call SKAN API.
     */
    renderClickThroughAppAds = (url: URL) => {
        this.logAPIInvocation('renderClickThroughAppAds');
        this.cms.sendMessage<MobileSupportedCommands['renderClickThroughAppAds']>('renderClickThroughAppAds', url.href);
    };

    /**
     * @func sendAdInfo
     * @desc [Internal & External] Sends information about creative ID / program ID to page, to send to AdFeedback team
     * @param {*} mobileAdsInfo - Information passed in by the creatives
     */
    sendAdInfo = (mobileAdsInfo: MobileAdsInfo) => {
        if (mobileAdsInfo) {
            this.cms.sendMessage<MobileSupportedCommands['getAdInfo']>('getAdInfo', { mobileAdsInfo: mobileAdsInfo });
        }
    };
    getPlacementInfo = () => {
        this.logAPIInvocation('getPlacementInfo');
        const placementInfo = {
            pageType: this.o.pageType as string,
            slotName: this.o.slotName as string,
            subPageType: this.o.subPageType as string,
        };
        return placementInfo;
    };
    // TODO Move this to public API
    getUserAgentInfo = (): string => {
        // TODO Eliminate the override here
        return window.navigator.userAgent;
    };
    /**
     * @deprecated Do not use this anymore.  Use direct APIs

     * @desc Returns the amzn-app-ctxt cookie if it exists
     * @returns {String} cookie - amzn-app-ctxt
     */
    getDocumentCookie = () => {
        this.logAPIInvocation('getDocumentCookie');
        const amaznAppContextCookieValue = this.o.amznAppContext ? `amzn-app-ctxt=${this.o.amznAppContext}` : undefined;
        return amaznAppContextCookieValue ?? '';
    };

    /**
     * TODO: deprecate this and replace it with shouldHaveAutoplayPermissionFromDevice
     * https://sim.amazon.com/issues/CPP-30396
     */
    getUserConnectionInfo = () => {
        this.logAPIInvocation('getUserConnectionInfo');
        return getUserNetworkConnectionInfo(this.o.amznAppContext);
    };
}
